import React from 'react';

/**
 * Handles processing a JSON file into chapter content data
 * @param file The file to process
 * @param setIsProcessing State setter for processing status
 * @param setContentData State setter for content data
 * @param setEditableChapterName State setter for editable chapter name
 * @param setIsNewContent State setter for new content flag
 * @param setShowContent State setter for show content flag
 * @param setSelectedChapterId State setter for selected chapter ID
 * @param onSelectChapter Callback for chapter selection
 */
export const processJsonFile = (
  file: File,
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setEditableChapterName: React.Dispatch<React.SetStateAction<string>>,
  setIsNewContent: React.Dispatch<React.SetStateAction<boolean>>,
  setShowContent: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedChapterId: React.Dispatch<React.SetStateAction<string>>,
  onSelectChapter: (chapterId: number) => void
): void => {
  if (!file) {
    alert('Please select a file first');
    return;
  }
  
  setIsProcessing(true);
  
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const content = e.target?.result;
      if (typeof content === 'string') {
        const parsed = JSON.parse(content);
        
        // Validate JSON structure
        if (!parsed.chapter || !Array.isArray(parsed.notes)) {
          throw new Error('Invalid JSON structure. Expected "chapter" and "notes" fields.');
        }
        
        // Set content data
        setContentData(parsed);
        setEditableChapterName(parsed.chapter);
        setIsNewContent(true);
        setShowContent(true);
        
        // Clear selection
        setSelectedChapterId('');
        onSelectChapter(0);
      }
    } catch (error) {
      console.error('Error processing JSON file:', error);
      alert(`Error processing file: ${error instanceof Error ? error.message : 'Invalid format'}`);
    } finally {
      setIsProcessing(false);
    }
  };
  
  reader.onerror = () => {
    alert('Error reading file');
    setIsProcessing(false);
  };
  
  reader.readAsText(file);
};

/**
 * Handles click of browse button to select a file
 * @param fileInputRef Reference to file input element
 */
export const handleBrowseClick = (
  fileInputRef: React.RefObject<HTMLInputElement>
): void => {
  if (fileInputRef.current) {
    fileInputRef.current.click();
  }
};